/**
 * `/internal/upstreams*` contract types (ADR-004 `gateway-worker /internal/*`).
 *
 * Caller: ops-api (CRUD management) and platform-api (catalog aggregation).
 *
 * Current model:
 *   - `upstream_configs` holds connection config keyed by `id`.
 *   - `upstream_model_capabilities` records discovered and probed upstream
 *     candidates.
 *   - SKU target/policy rows attach capabilities to tenant routing.
 *
 * Soft delete: migration `0002` adds `upstream_configs.deleted_at TEXT`. List,
 * get, patch, and catalog endpoints filter out rows where `deleted_at IS NOT
 * NULL`. `DELETE /internal/upstreams/:id` sets `deleted_at = CURRENT_TIMESTAMP`.
 */

import type { EgressConfig } from './egress-config.ts';
import type { GatewayMutationReceipt } from './gateway-mutation-receipts.ts';

/**
 * `kind` enumerates how the router dispatches to the upstream. Phase 1 only ships
 * `'forward'` (direct HTTP forward to an Anthropic-compatible endpoint). ADR-003
 * reserves `'pool-http'` and `'byok'` for future phases — accepted on the wire
 * so ops can start populating them, but gateway routing ignores them until the
 * corresponding executors land.
 */
export type UpstreamKind = 'forward' | 'pool-http' | 'byok';

/**
 * Protocol the *router side* speaks to this upstream over. Each
 * One upstream may expose multiple protocol capabilities and supply-side
 * channels; SKU targets select the concrete capability identity.
 *
 * `'grok_video_v1'` is a first-class supply ladder (#2138 design lock): the
 * Grok video wire is not OpenAI-compatible, so it is NOT parked on `'openai'`
 * the way `'ark_video_v1'` (sales form) maps to an OpenAI-compatible UCloud
 * task base. Chat discovery, CLI probes, and the sidecar lane set stay the
 * three chat members below — they are different closed sets.
 *
 * Single source of truth: {@link KNOWN_UPSTREAM_PROTOCOLS}. Runtime copies
 * (`PROTOCOL_VALUES`, capability `PROTOCOLS`) must derive from it.
 */
export const KNOWN_UPSTREAM_PROTOCOLS = [
  'anthropic',
  'openai',
  'gemini',
  'grok_video_v1',
  'grok_image_v1',
] as const;

export type UpstreamProtocol = (typeof KNOWN_UPSTREAM_PROTOCOLS)[number];

/**
 * Chat-shaped upstream wires. `/v1/models` discovery, CLI probes, and the
 * sidecar v1 API lanes stay on this set — grok video is manual capabilities
 * and never hits those surfaces.
 */
export const CHAT_UPSTREAM_PROTOCOLS = [
  'anthropic',
  'openai',
  'gemini',
] as const satisfies readonly UpstreamProtocol[];

export type ChatUpstreamProtocol = (typeof CHAT_UPSTREAM_PROTOCOLS)[number];

export function isUpstreamProtocol(value: unknown): value is UpstreamProtocol {
  return typeof value === 'string'
    && (KNOWN_UPSTREAM_PROTOCOLS as readonly string[]).includes(value);
}

export function isChatUpstreamProtocol(value: unknown): value is ChatUpstreamProtocol {
  return typeof value === 'string'
    && (CHAT_UPSTREAM_PROTOCOLS as readonly string[]).includes(value);
}

/**
 * Credential carrier used only for an explicitly configured model-list
 * request. The set is intentionally closed: a catalog path may select the
 * right standard carrier without turning control-plane configuration into an
 * arbitrary secret-bearing header injection surface.
 */
export type UpstreamModelListAuth = 'anthropic' | 'bearer' | 'google-api-key';

/**
 * Upstream model discovery is independent from the generation base URL.
 *
 * - `compatible` preserves the protocol-derived `/v1/models` and
 *   `/v1beta/models` behavior used by existing upstreams.
 * - `list` uses one explicit same-origin path and JSON array location.
 * - `manual` performs no remote list request; callers must supply model ids to
 *   the capability-discovery operation.
 */
export type UpstreamModelDiscoveryConfig =
  | { readonly mode: 'compatible' }
  | {
      readonly mode: 'list';
      /** Origin-relative absolute path, optionally including a query string. */
      readonly path: string;
      /** JSON object keys leading to the array of model items. */
      readonly response_path: readonly string[];
      readonly auth: UpstreamModelListAuth;
    }
  | { readonly mode: 'manual' };

/**
 * Concrete OpenAI endpoint family a direct OpenAI egress profile supports.
 * Carried by the typed `EgressConfig` document (and mirrored to the
 * dedicated `wire_api` column) so key/channel bindings can override it
 * without splitting the public `protocol = 'openai'` pricing dimension.
 */
export type OpenAIWireApi = 'chat_completions' | 'responses' | 'both';

export const KNOWN_OPENAI_WIRE_APIS = [
  'chat_completions',
  'responses',
  'both',
] as const satisfies readonly OpenAIWireApi[];

/**
 * Canonical upstream config row as returned by `GET /internal/upstreams`.
 *
 * `name` corresponds to the display label shown in ops-console. It maps to the
 * `upstream_configs.name` column in SQL (ADR-003).
 *
 * `endpoint_url` is the HTTPS origin the executor will `fetch()` against.
 * Path suffix (`/v1/messages` etc.) is executor-decided, not stored here.
 *
 * `has_auth_secret` / `auth_secret_preview` let the ops surface confirm
 * whether a direct upstream credential exists without ever reading the full
 * secret back out of SQL.
 */
export interface UpstreamConfig {
  id: string;
  name: string;
  kind: UpstreamKind;
  endpoint_url: string;
  /** Defaults to `{ mode: 'compatible' }` for pre-existing rows. */
  model_discovery?: UpstreamModelDiscoveryConfig;
  /** Runtime-only in gateway internals; control-plane reads return an empty string. */
  auth_secret_ref: string;
  /** Stable support-diagnostics source number rendered as S001, S002, ... */
  diagnostic_source_no: number;
  has_auth_secret: boolean;
  auth_secret_preview: string | null;
  allow_cookie_passthrough: boolean;
  enabled: boolean;
  priority: number;
  markup_pct: number;
  /**
   * Ops-api list enrichment. Gateway's canonical config row does not own
   * accounts or funding, so these fields are optional for callers that read
   * gateway directly.
   */
  account_count?: number;
  active_funding_batch_count?: number;
  active_funding_remaining_usd_cents?: number;
  /**
   * Network home of `endpoint_url` (`cn` / `jp` / `us`). Default `jp`
   * is the behaviour-preserving backfill (ADR-014 §1). Gateway always
   * returns this after #1979; older fixtures may omit it.
   */
  region?: string;
  /**
   * Optional channel-level pin of an egress-exit link. Empty/null means
   * the scored dispatcher chooses. Pin is an escape hatch, not a
   * health-check bypass (ADR-014 §4).
   */
  pinned_egress_link_id?: string | null;
  /**
   * Pool upstream whose account picker keys on the client session id
   * (#2325). NULL/false = not a pool; per-session re-homing stays off.
   */
  session_pool?: boolean | null;
  created_at: string;
  updated_at: string;
}

/**
 * `POST /internal/upstreams` request body.
 *
 * `name`, `kind`, and `endpoint_url` are required; every other
 * field falls back to the SQL column default.
 */
export interface CreateUpstreamRequest {
  name: string;
  kind: UpstreamKind;
  endpoint_url: string;
  model_discovery?: UpstreamModelDiscoveryConfig;
  /** Optional write-only direct secret value. Never returned verbatim. */
  auth_secret_ref?: string;
  allow_cookie_passthrough?: boolean;
  enabled?: boolean;
  priority?: number;
  markup_pct?: number;
  region?: string;
  pinned_egress_link_id?: string | null;
  session_pool?: boolean | null;
}

export interface CreateUpstreamResponse {
  upstream: UpstreamConfig;
  /** Present when create atomically changes model-discovery configuration. */
  mutation_receipt?: GatewayMutationReceipt;
}

export interface ListUpstreamsResponse {
  upstreams: UpstreamConfig[];
}

export interface GetUpstreamResponse {
  upstream: UpstreamConfig;
}

/**
 * `PATCH /internal/upstreams/:id` request body. Every field is optional; only
 * provided fields are updated. Primary key (`id`), `created_at`, and
 * `deleted_at` cannot be patched.
 */
export interface UpdateUpstreamRequest {
  name?: string;
  kind?: UpstreamKind;
  endpoint_url?: string;
  model_discovery?: UpstreamModelDiscoveryConfig;
  /** Optional write-only direct secret value. Never returned verbatim. */
  auth_secret_ref?: string;
  allow_cookie_passthrough?: boolean;
  enabled?: boolean;
  priority?: number;
  markup_pct?: number;
  region?: string;
  pinned_egress_link_id?: string | null;
  session_pool?: boolean | null;
}

export interface UpdateUpstreamResponse {
  upstream: UpstreamConfig;
  /** Present when patch atomically changes model-discovery configuration. */
  mutation_receipt?: GatewayMutationReceipt;
}

/**
 * Optional optimistic precondition for terminal upstream retirement.
 *
 * General operator deletes may omit it. Automated cutovers provide the exact
 * enabled legacy binding set they inspected; gateway rechecks that set inside
 * the same serialized owner transaction before disabling any binding or
 * creating an audit receipt.
 */
export interface DeleteUpstreamRequest {
  expected_enabled_route_binding_ids?: readonly string[];
}

export interface DeleteUpstreamResponse {
  deleted: true;
  id: string;
  /** Exact legacy route bindings that were enabled before this retirement. */
  retired_route_binding_ids: readonly string[];
  retired_route_binding_count: number;
  /** Must be zero for the retired upstream after the owner transaction. */
  enabled_route_binding_count_after: number;
  routing_revision: string;
  /** True when the upstream was already retired and no new mutation occurred. */
  already_deleted?: boolean;
  /** Owner-committed evidence for a first successful retirement. */
  mutation_receipt?: GatewayMutationReceipt;
}

/**
 * Global reusable egress template. Channel-specific instances live on
 * key/channel bindings.
 *
 * `egress_config` (the typed document, egress-config.ts) is the
 * authoritative shape since router#830 R1 phase C2; it is `null` only for
 * rows whose stored document is missing or unparseable (pre-backfill
 * stragglers). The legacy dialect fields were deleted with the R3 sweep
 * (router#832) — consumers read the typed document (`lane`, `mode`, ...).
 */
/**
 * One key/channel binding that inherits a template
 * (`upstream_key_channel_bindings.egress_profile_id`). These are the rows
 * behind both the delete guard (`EGRESS_PROFILE_IN_USE`) and the
 * "saving affects inheriting bindings" warning in the console.
 */
export interface EgressProfileUsageBinding {
  readonly upstream_id: string;
  /** Null when the owning upstream row is gone (soft-deleted stragglers). */
  readonly upstream_name: string | null;
  readonly key_bucket_id: string;
  readonly key_bucket_label: string;
  readonly channel_id: string;
  readonly channel_slug: string;
}

/**
 * Configuration rows referencing a template. Deliberately excludes
 * request-history rows (`openai_response_routes`, `usage_logs`) — those
 * record what a past request used, not what inherits the template now.
 */
export interface EgressProfileUsage {
  readonly key_channel_bindings: readonly EgressProfileUsageBinding[];
  /** Active SKU targets inheriting this key/channel profile. */
  readonly sku_target_count: number;
}

export interface EgressProfileRecord {
  readonly id: string;
  readonly name: string;
  readonly egress_config: EgressConfig | null;
  /**
   * Resolved OpenAI wire API. Empty/NULL columns echo `'both'`:
   * empty column and explicit both are behavior-equivalent, so they are not distinguished.
   */
  readonly wire_api: OpenAIWireApi;
  readonly enabled: boolean;
  readonly usage: EgressProfileUsage;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListEgressProfilesResponse {
  readonly egress_profiles: readonly EgressProfileRecord[];
}

export interface CreateEgressProfileRequest {
  readonly name: string;
  /** Typed egress config document; validated with `parseEgressConfig`. */
  readonly egress_config: EgressConfig;
  readonly enabled?: boolean;
  /** Omit to keep the column default/NULL (`both` on read). */
  readonly wire_api?: OpenAIWireApi;
}

export interface CreateEgressProfileResponse {
  readonly egress_profile: EgressProfileRecord;
}

export interface UpdateEgressProfileRequest {
  readonly name?: string;
  /** Whole-document replacement; validated with `parseEgressConfig`. */
  readonly egress_config?: EgressConfig;
  readonly enabled?: boolean;
  /** Omit to keep the stored value. */
  readonly wire_api?: OpenAIWireApi;
}

export interface UpdateEgressProfileResponse {
  readonly egress_profile: EgressProfileRecord;
}

export interface DeleteEgressProfileResponse {
  readonly ok: true;
  readonly id: string;
}

/**
 * `GET /internal/upstreams/catalog` response. ADR-004 defines this as the
 * input to platform-api's tenant-facing `GET /v1/models` aggregator.
 *
 * `upstreams` is the flat list of non-deleted, enabled upstreams. `models` is
 * the deduped set of live SKU-target capabilities across those upstreams.
 * The `channel` field is the SKU namespace slug retained for display only;
 * it is not a routing key. `entries` preserves the upstream→SKU supply view.
 */
export interface UpstreamCatalogModel {
  vendor: string;
  channel: string;
  model: string;
  protocol: UpstreamProtocol;
  upstream_model_name: string;
}

export interface UpstreamCatalogUpstream {
  id: string;
  name: string;
  kind: UpstreamKind;
  enabled: boolean;
  priority: number;
}

export interface UpstreamCatalogEntry {
  upstream: UpstreamCatalogUpstream;
  models: UpstreamCatalogModel[];
}

export interface UpstreamCatalogResponse {
  /** Deduped live SKU supply set across every live upstream. */
  models: UpstreamCatalogModel[];
  /** Per-upstream view: lets platform-api show which upstream(s) serve a model. */
  entries: UpstreamCatalogEntry[];
}
